import {
  ActionButtons,
  Footer,
  LoadingV2,
  SchemaFactoryV2,
  SectionPlaceholder,
  useFieldSchemaV2,
} from "@ucl/library";
import {
  TaskExecutionModuleFormProps,
  TaskExecutionModuleProps,
} from "../TaskExecutionModuleProps";
import { useEffect, useState } from "react";
import { useBoltApiClient } from "@bolt/core";

import classNames from "classnames";

interface DataCollectionTaskExecutionTypeFieldSchema {
  DataCollectionTaskExecutionType__RequiredFields: string;
}

export const DataCollectionTaskExecutionTypeModule: React.FC<
  TaskExecutionModuleProps
> = (props) => {
  // Load the module based on the taskExecutionType
  if (!props.formRegistry) {
    console.error(
      `Form Registry is not defined in DataCollectionTaskExecutionTypeModule`
    );
    return <SectionPlaceholder label="Task could not be loaded" />;
  }

  const boltApiClient = useBoltApiClient();
  const [formComponents, setFormComponents] = useState<
    {
      ModelName: string;
      Title: string;
      AvailableFields: string[];
      FormComponent: React.FC<TaskExecutionModuleFormProps>;
    }[]
  >([]);

  const [schemaFactory, setSchemaFactory] = useState<SchemaFactoryV2>();
  const { getAsyncSchemaFactory } = useFieldSchemaV2("tasks/field-schema");

  const getSchema = async () => {
    const schemaFactory = await getAsyncSchemaFactory(boltApiClient);
    return schemaFactory;
  };

  useEffect(() => {
    if (!schemaFactory) {
      getSchema().then((factory) => {
        setSchemaFactory(factory);
        setupForm(factory);
      });
    }
  }, []);

  const setupForm = (factory: SchemaFactoryV2) => {
    // Get Data Collection Fields
    // Cast the taskExecutionTypeFields to the DataCollectionTaskExecutionTypeFieldSchema
    const value =
      props.taskExecutionTypeFields as unknown as DataCollectionTaskExecutionTypeFieldSchema;

    const requiredFields =
      value.DataCollectionTaskExecutionType__RequiredFields.split(",");

    // Get All Schemas from the factory
    const allSchemas = factory.getFieldSchemas();

    // For each field, find it on the allSchemas and get the modelName
    let requireFieldSchema: Record<string, string> = {};
    for (const field of requiredFields) {
      Object.keys(allSchemas).forEach((modelName) => {
        const fieldSchema = allSchemas[modelName][field];
        if (fieldSchema) {
          requireFieldSchema[field] = modelName;
        }
      });
    }

    // Group the fields by modelName
    // Create a Record<string, string[]> where the key is the modelName and the value is an array of field keys (string)
    const groupFieldsByModelName: Record<string, string[]> = {};
    for (const field in requireFieldSchema) {
      const modelName = requireFieldSchema[field];
      if (!groupFieldsByModelName[modelName]) {
        groupFieldsByModelName[modelName] = [];
      }
      groupFieldsByModelName[modelName].push(field);
    }

    // For each group, get the form component from the registry
    const formComponents: {
      ModelName: string;
      Title: string;
      AvailableFields: string[];
      FormComponent: React.FC<TaskExecutionModuleFormProps>;
    }[] = [];
    for (const modelName in groupFieldsByModelName) {
      const ModuleComponent = props.formRegistry?.[modelName]
        .FormComponent as React.FC<Partial<TaskExecutionModuleFormProps>>;
      if (!ModuleComponent) {
        console.error(
          `Form component for model ${modelName} is not defined in registry`
        );
      }
      formComponents.push({
        ModelName: modelName,
        Title: props.formRegistry?.[modelName]?.Title || "",
        AvailableFields: groupFieldsByModelName[modelName],
        FormComponent: ModuleComponent,
      });
    }

    setFormComponents(formComponents);
  };

  if (!formComponents) {
    return <LoadingV2 />;
  }

  return (
    <section className="data-collection-task-execution-type-module">
      <div className="data-collection-forms">
        {formComponents.map(
          ({ ModelName, Title, FormComponent, AvailableFields }, index) => (
            <div
              key={index}
              className={classNames(
                "form-component-container",
                `form-component-container-${ModelName}`
              )}
            >
              {Title && <h3>{Title}</h3>}
              <FormComponent
                entityId={props.entityId}
                entityType={props.entityType}
                parentEntityId={props.parentEntityId}
                parentEntityType={props.parentEntityType}
                availableFields={AvailableFields}
                isReadOnly={props.isReadOnly}
              />
            </div>
          )
        )}
      </div>

      <Footer>
        {!props.isReadOnly ? (
          <ActionButtons
            actions={[
              {
                text: "Complete",
                isPrimary: true,
                onActionClick: () => {
                  props.onTaskComplete?.(props.taskExecutionTypeFields);
                },
              },
            ]}
          />
        ) : (
          <></>
        )}
      </Footer>
    </section>
  );
};
